import { Alert, Button, Modal } from 'react-bootstrap';
import { FEMA_ROTATION_SENTENCE, FEMA_UPLOAD_STEPS } from '../../config/femaCredentials.config';
import {
  describeFemaTenantRefusal,
  environmentClassLabel,
} from '../../utils/credentialState.utils';
import FileStep from './components/FileStep/FileStep';
import PasswordStep from './components/PasswordStep/PasswordStep';
import ReviewStep from './components/ReviewStep/ReviewStep';
import ValidateStep from './components/ValidateStep/ValidateStep';
import { useCredentialUpload } from './hooks/useCredentialUpload';

/**
 * The FE-11 guided upload (design read §3.4, §3.5): File → Passwords → Review → Validate →
 * Enabled. `replace` runs the same steps against the replacement slot and shows the rotation
 * sentence. Closing the dialog unmounts the hook, which empties the password ref.
 *
 * @param {{
 *   tenant: import('types/femaTenant.types').FemaTenantSummary,
 *   replace?: boolean,
 *   onClose: () => void,
 * }} props
 */
const CredentialUploadWizard = ({ tenant, replace = false, onClose }) => {
  const upload = useCredentialUpload(tenant);
  const classLabel = environmentClassLabel(tenant.environment);
  const currentIndex = FEMA_UPLOAD_STEPS.indexOf(upload.step);

  return (
    <Modal show onHide={onClose} size="lg" backdrop="static" aria-labelledby="fema-upload-title">
      <Modal.Header closeButton>
        <Modal.Title id="fema-upload-title">
          {replace ? 'Replace the credential' : 'Upload a credential'} — {classLabel}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ol className="list-inline small mb-3" aria-label="Upload steps">
          {FEMA_UPLOAD_STEPS.map((step, index) => (
            <li
              key={step}
              className={`list-inline-item ${index === currentIndex ? 'fw-bold' : 'text-muted'}`}
              aria-current={index === currentIndex ? 'step' : undefined}
            >
              {index + 1}. {step}
            </li>
          ))}
        </ol>

        {replace ? <Alert variant="info">{FEMA_ROTATION_SENTENCE}</Alert> : null}

        {upload.error ? (
          <Alert variant="danger" role="alert" data-testid="fema-credential-error">
            {describeFemaTenantRefusal(upload.error)}
          </Alert>
        ) : null}

        {upload.step === 'File' ? (
          <FileStep
            keyAlias={upload.keyAlias}
            onKeyAliasChange={upload.setKeyAlias}
            onChoose={upload.chooseFiles}
          />
        ) : null}
        {upload.step === 'Passwords' ? <PasswordStep upload={upload} /> : null}
        {upload.step === 'Review' ? <ReviewStep upload={upload} /> : null}
        {upload.step === 'Validate' ? <ValidateStep upload={upload} /> : null}
        {upload.step === 'Enabled' ? (
          <Alert variant="success">
            {replace
              ? 'The new certificate is active.'
              : 'The credential is validated and enabled.'}
          </Alert>
        ) : null}
      </Modal.Body>
      {upload.step === 'Enabled' ? (
        <Modal.Footer>
          <Button onClick={onClose}>Close</Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

export default CredentialUploadWizard;
